// Utility function zum sicheren Rendern von HTML mit strong Tags
import React from "react";

interface SafeHTMLRendererProps {
  content: string;
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
  id?: string;
  enableLinkPrefetch?: boolean; // Enable Next.js Link with prefetch for internal links
}

/**
 * Sicherer HTML-Renderer für Texte mit <strong> Tags aus JSON
 * Verwendet dangerouslySetInnerHTML nur für vertrauenswürdige Inhalte
 * Server-side safe für korrekte Hydration
 *
 * @param enableLinkPrefetch - Wenn true, werden interne Links zu Next.js Link Components konvertiert
 */
export const SafeHTMLRenderer: React.FC<SafeHTMLRendererProps> = ({
  content,
  className = "",
  tag: Tag = "span",
  id,
  enableLinkPrefetch = true,
}) => {
  void enableLinkPrefetch;

  // Sanitize function - entfernt alle nicht erlaubten Tags
  const sanitizeHTML = (html: string): string => {
    if (!html || typeof html !== "string") return "";

    // Einfache Sanitization - entfernt alle Tags außer den erlaubten
    // Erlaubte Tags: strong, em, br, span, a (für interne Links), abbr (für Glossar-Tooltips)
    // Hinweis: Attribute auf erlaubten Tags bleiben erhalten (z. B. <abbr title="...">),
    // weil der Regex bei Treffer der Allow-List den Tag unverändert lässt.
    return html
      .replace(/<(?!\/?(?:strong|em|br|span|a|abbr)\b)[^>]*>/gi, "") // Entfernt nicht erlaubte öffnende Tags
      .replace(/<\/(?!strong|em|br|span|a|abbr)\b[^>]*>/gi, ""); // Entfernt nicht erlaubte schließende Tags
  };

  // Verwende suppressHydrationWarning für bessere Hydration-Kontrolle
  const sanitizedContent = sanitizeHTML(content);

  return React.createElement(Tag, {
    id,
    className,
    dangerouslySetInnerHTML: { __html: sanitizedContent },
    suppressHydrationWarning: true, // Unterdrückt Hydration-Warnungen für diesen spezifischen Fall
  });
};

/**
 * Hook zum Laden der home.json Daten
 */
export const useHomeData = () => {
  const [data, setData] = React.useState<HomeData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch("/src/data/home.json");
        if (!response.ok) {
          throw new Error(`Failed to load home data: ${response.statusText}`);
        }
        const homeData = await response.json();
        setData(homeData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
        console.error("Error loading home data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return { data, loading, error };
};

/**
 * Alternative: Direkter Import der JSON-Daten (für statische Inhalte)
 */
import homeData from "../data/pages/home.json";
import type { HomeData } from "../types/home";
export { homeData };
